export const {
  defaultLanguageCode,
  supportedLanguagesCodes,
  supportedLanguagesLookup,
} = require('./i18nConstants');

export const ORDER_STATUSES = {
  ORDER_REQUESTED: 'ORDER_REQUESTED',
  ORDER_PLACED: 'ORDER_PLACED',
  ORDER_REJECTED: 'ORDER_REJECTED',
  KIT_SHIPPED: 'KIT_SHIPPED',
  KIT_DELIVERED: 'KIT_DELIVERED',
  KIT_UNDELIVERED: 'KIT_UNDELIVERED',
  SAMPLE_UNRETURNED: 'SAMPLE_UNRETURNED',
  SAMPLE_RETURNED: 'SAMPLE_RETURNED',
  SAMPLE_INVALIDATED: 'SAMPLE_INVALIDATED',
  SAMPLE_RECEIVED: 'SAMPLE_RECEIVED',
  SAMPLE_UNRECEIVED: 'SAMPLE_UNRECEIVED',
  SAMPLE_ANALYZED: 'SAMPLE_ANALYZED',
  SAMPLE_DELIVERED: 'SAMPLE_DELIVERED',
  RESULTS_REPORTED: 'RESULTS_REPORTED',
  RESULTS_DELIVERED: 'RESULTS_DELIVERED',
  RESULTS_UNDELIVERED: 'RESULTS_UNDELIVERED',
  COMPLETE: 'COMPLETE',
  CANCELLED: 'CANCELLED',
  ERROR: 'ERROR',
};

export const ORDERED_ORDER_STATUSES = [
  ORDER_STATUSES.ORDER_REQUESTED,
  ORDER_STATUSES.ORDER_PLACED,
  ORDER_STATUSES.KIT_SHIPPED,
  ORDER_STATUSES.KIT_DELIVERED,
  ORDER_STATUSES.KIT_UNDELIVERED,
  ORDER_STATUSES.SAMPLE_UNRETURNED,
  ORDER_STATUSES.SAMPLE_RETURNED,
  ORDER_STATUSES.SAMPLE_INVALIDATED,
  ORDER_STATUSES.SAMPLE_RECEIVED,
  ORDER_STATUSES.SAMPLE_UNRECEIVED,
  ORDER_STATUSES.SAMPLE_ANALYZED,
  ORDER_STATUSES.RESULTS_REPORTED,
  ORDER_STATUSES.RESULTS_DELIVERED,
  ORDER_STATUSES.RESULTS_UNDELIVERED,
  ORDER_STATUSES.COMPLETE,
  ORDER_STATUSES.ORDER_REJECTED,
  ORDER_STATUSES.CANCELLED,
  ORDER_STATUSES.ERROR,
];

export const LIFECYCLE_STATUSES = [
  {
    value: '32',
    label: 'Abandoned',
  },
  {
    value: '64',
    label: 'Retired',
  },
  {
    value: '128',
    label: 'Removed',
  },
  {
    value: '256',
    label: 'Out of Service',
  },
];

export const FEATURE_CONSTANTS = {
  APPLICATION_FEATURE_NAME: 'leadCAST',
  CUSTOMER_PORTAL: 'Customer Portal',
  FIELD_INSPECTION: 'Field Inspection',
  LEAD_ML: 'Predict',
  PUBLIC_MAP: 'Public Map',
  REPLACEMENT: 'Replacement',
  RIGHT_OF_ENTRY: 'Right of Entry',
  SAMPLING: 'Sampling',
  SCHOOL_AND_DAYCARE: 'S&D Sampling',
};

export const FORM_TYPES = {
  RIGHT_OF_ENTRY: 'Right of Entry',
};

export const CUSTOMER_TYPES = {
  CUSTOMER: 'Customer',
};

export const LICENSE_LEVEL_CONSTANTS = {
  PLUS: 'Plus',
};
export type LicenseLevel = (typeof LICENSE_LEVEL_CONSTANTS)[keyof typeof LICENSE_LEVEL_CONSTANTS];

export const SAMPLE_ANALYTE_CONSTANTS = {
  LEAD: 'lead',
  COPPER: 'copper',
} as const;
export type SampleAnalyte =
  (typeof SAMPLE_ANALYTE_CONSTANTS)[keyof typeof SAMPLE_ANALYTE_CONSTANTS];

export const EXCEEDANCE_DEFAULTS = {
  DEFAULT_EXCEEDANCE_THRESHOLDS: {
    lead: 15,
    copper: 1.3,
  },
} as const;

export const REPLACEMENT_WORKFLOW_STEPS = {
  PREREPLACEMENT: 'Prereplacement',
  REPLACEMENT: 'Replacement',
  PENDING_ROE: 'Pending ROE',
};

export const INSPECTION_STATUSES = {
  VERIFIED: 'Verified',
  REJECTED: 'Rejected',
  REJECTED_CUSTOMER: 'Rejected (Rejected Customer)',
  PENDING_APPROVAL: 'Pending Approval',
  PENDING_APPROVAL_UNVERIFIED: 'Pending Approval (Unverified Customer)',
  SENT_BACK: 'Sent Back for Resubmission',
};

export const FIELD_INSPECTION_HELP_LINK =
  'https://scribehow.com/page/How_to_check_a_water_service_line_material__BFlIlCp7SA6lFZywz4hiVA';

export const LEAD_STATUSES = {
  UNKNOWN: 'Lead Status Unknown',
  NON_LEAD: 'Non-Lead',
  LEAD: 'Lead',
  GALV_REQ_REPLACEMENT: 'Galv Req Replacement',
} as const;
export type LeadStatuses = (typeof LEAD_STATUSES)[keyof typeof LEAD_STATUSES];

export const REPLACEMENT_STATUSES = {
  PENDING_REVIEW: 'Pending Review',
  VERIFIED_NON_LEAD: 'Verified Non-Lead',
  CONFIRMED_LEAD: 'Confirmed Lead',
  PARTIAL_REPLACEMENT_CONDUCTED: 'Partial Replacement Conducted',
  GALV_REPLACEMENT_COMPLETED: 'Galv Replacement Completed',
  REPLACEMENT_COMPLETED: 'Replacement Completed',
} as const;

export const REPLACEMENT_INSPECTION_STATUSES = {
  DRAFT: 'Draft',
  PENDING_REVIEW: 'Pending Review',
  SENT_BACK_FOR_CORRECTIONS: 'Sent Back For Corrections',
  APPROVED: 'Approved',
  APPROVED_WITH_SUGGESTIONS: 'Approved with Suggestions',
  RESUBMITTED: 'Resubmitted',
} as const;

export type ReplacementInspectionStatus =
  (typeof REPLACEMENT_INSPECTION_STATUSES)[keyof typeof REPLACEMENT_INSPECTION_STATUSES];

export const statusesEligibleForInspection = [
  REPLACEMENT_INSPECTION_STATUSES.DRAFT,
  REPLACEMENT_INSPECTION_STATUSES.SENT_BACK_FOR_CORRECTIONS,
  REPLACEMENT_INSPECTION_STATUSES.APPROVED_WITH_SUGGESTIONS,
] as const;

export type EligibleForInspectionStatus = (typeof statusesEligibleForInspection)[number];

export const CONTACT_TYPES = {
  HOME_OWNER: 'Home Owner',
  TENANT: 'Tenant',
  PROPERTY_MANAGER: 'Property Manager',
  UNKNOWN: 'Unknown',
} as const;
export type ContactType = (typeof CONTACT_TYPES)[keyof typeof CONTACT_TYPES];
export const authorizedROEContactTypes: ContactType[] = [
  CONTACT_TYPES.HOME_OWNER,
  CONTACT_TYPES.PROPERTY_MANAGER,
];

export const RIGHT_OF_ENTRY_STATUS_CONSTANTS = {
  APPROVED: 'Approved',
  DENIED: 'Denied',
  INVALIDATED: 'Invalidated',
  NOT_SIGNED: 'Not Signed',
  PENDING_REVIEW: 'Pending Review',
};

export type RightOfEntryStatusType =
  (typeof RIGHT_OF_ENTRY_STATUS_CONSTANTS)[keyof typeof RIGHT_OF_ENTRY_STATUS_CONSTANTS];

export const FILE_LOCATION_CONSTANTS = {
  COMMUNICATION_EVENTS: 'communicationEvents',
  INSPECTIONS: 'inspections',
  PRE_REPLACEMENT_INSPECTIONS: 'preReplacementInspections',
  POST_REPLACEMENT_INSPECTIONS: 'postReplacementInspections',
  SAMPLING_INSTRUCTIONS: 'samplingInstructions',
  RIGHT_OF_ENTRY: 'rightOfEntry',
  DIGITAL_WATER_SAMPLE_FORMS: 'digitalWaterSampleForms',
};

export type FileLocationType =
  (typeof FILE_LOCATION_CONSTANTS)[keyof typeof FILE_LOCATION_CONSTANTS];

export const SAMPLING_PROGRAM_TYPES_CONSTANTS = {
  COMPLIANCE: 'Compliance',
  POST_REPLACEMENT: 'Post Replacement',
  CUSTOMER_REQUESTED: 'Customer Requested',
  SCHOOLS_AND_DAYCARE: 'Schools and Daycare',
};
